// Shared placement of a local 2D article in a millimetre plan.
// Retains legacy SVG placement defaults; geometry remains renderer-independent.
function finiteNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function positiveNumber(value, fallback = 1) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
}

function rotatePoint(point, center, degrees) {
  const angle = finiteNumber(degrees) * Math.PI / 180;
  if (!angle) return { x: finiteNumber(point?.x), z: finiteNumber(point?.z) };
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dx = finiteNumber(point?.x) - finiteNumber(center?.x);
  const dz = finiteNumber(point?.z) - finiteNumber(center?.z);
  return {
    x: finiteNumber(center?.x) + dx * cos - dz * sin,
    z: finiteNumber(center?.z) + dx * sin + dz * cos
  };
}

function sourceBoundsFromAsset(asset, targetWidth, targetDepth) {
  const raw = asset?.contentBounds;
  const minX = finiteNumber(raw?.minX);
  const minZ = finiteNumber(raw?.minZ);
  const width = positiveNumber(raw?.width, targetWidth);
  const depth = positiveNumber(raw?.depth, targetDepth);
  return { minX, minZ, width, depth, maxX: minX + width, maxZ: minZ + depth };
}

export function createPlanAssetPlacement(asset, options = {}) {
  const targetWidth = positiveNumber(options.widthMm, positiveNumber(asset?.widthMm, 1));
  const targetDepth = positiveNumber(options.depthMm, positiveNumber(asset?.depthMm, targetWidth));
  const sourceBounds = sourceBoundsFromAsset(asset, targetWidth, targetDepth);
  const scaleX = targetWidth / positiveNumber(sourceBounds.width, targetWidth);
  const scaleZ = options.scaleMode === 'stretch'
    ? targetDepth / positiveNumber(sourceBounds.depth, targetDepth)
    : scaleX;
  const renderWidth = sourceBounds.width * scaleX;
  const renderDepth = sourceBounds.depth * scaleZ;
  const offsetX = finiteNumber(options.offsetX) + (targetWidth - renderWidth) / 2;
  const offsetZ = finiteNumber(options.offsetZ) + (targetDepth - renderDepth) / 2;
  const x = finiteNumber(options.x);
  const z = finiteNumber(options.z);
  return {
    x,
    z,
    targetWidth,
    targetDepth,
    sourceBounds,
    scaleX,
    scaleZ,
    renderWidth,
    renderDepth,
    originX: x + offsetX,
    originZ: z + offsetZ,
    offsetX,
    offsetZ,
    rotationDeg: finiteNumber(options.rotationDeg),
    rotationCenter: {
      x: finiteNumber(options.rotationCenter?.x, x + targetWidth / 2),
      z: finiteNumber(options.rotationCenter?.z, z + targetDepth / 2)
    },
    mirrorX: options.mirrorX === true,
    mirrorZ: options.mirrorZ === true
  };
}

export function planAssetLocalPointToWorld(point, placement) {
  const source = placement.sourceBounds;
  let dx = (finiteNumber(point?.x) - source.minX) * placement.scaleX;
  let dz = (finiteNumber(point?.z) - source.minZ) * placement.scaleZ;
  if (placement.mirrorX) dx = placement.renderWidth - dx;
  if (placement.mirrorZ) dz = placement.renderDepth - dz;
  return rotatePoint({
    x: placement.originX + dx,
    z: placement.originZ + dz
  }, placement.rotationCenter, placement.rotationDeg);
}

export function planAssetWorldPointToLocal(point, placement) {
  const unrotated = rotatePoint(point, placement.rotationCenter, -placement.rotationDeg);
  let dx = unrotated.x - placement.originX;
  let dz = unrotated.z - placement.originZ;
  if (placement.mirrorX) dx = placement.renderWidth - dx;
  if (placement.mirrorZ) dz = placement.renderDepth - dz;
  return {
    x: placement.sourceBounds.minX + dx / positiveNumber(placement.scaleX, 1),
    z: placement.sourceBounds.minZ + dz / positiveNumber(placement.scaleZ, 1)
  };
}

